import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Check, Plus, Building2 } from 'lucide-react';
import { useOrgStore } from '../../store/orgStore';

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  MEMBER: 'Member',
};

export default function OrgSwitcher() {
  const navigate = useNavigate();
  const { activeOrgId, organizations, switchOrg } = useOrgStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const activeOrg = organizations.find((o) => o.id === activeOrgId);

  // 외부 클릭 시 닫기
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleSwitch = (orgId: string) => {
    switchOrg(orgId);
    setOpen(false);
    navigate('/');
  };

  if (organizations.length === 0) return null;

  return (
    <div ref={ref} className="relative px-3 w-full">
      {/* Trigger */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left
          hover:bg-gray-100 transition-colors"
      >
        <div className="size-7 rounded-md bg-primary-100 flex items-center justify-center shrink-0">
          <Building2 className="size-4 text-primary-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {activeOrg?.name ?? '조직 선택'}
          </p>
        </div>
        <ChevronDown
          className={`size-4 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute left-3 right-3 top-full z-50 mt-1
          bg-white shadow-lg rounded-lg border border-gray-200 py-1 max-h-64 overflow-y-auto">
          {organizations.map((org) => {
            const isActive = org.id === activeOrgId;
            return (
              <button
                key={org.id}
                onClick={() => handleSwitch(org.id)}
                title={org.name}
                className={`w-full flex items-start gap-2 px-3 py-2.5 text-left
                  hover:bg-gray-50 transition-colors
                  ${isActive ? 'bg-primary-50/60' : ''}`}
              >
                {isActive ? (
                  <Check className="size-4 text-primary-600 shrink-0 mt-0.5" />
                ) : (
                  <span className="size-4 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm truncate ${isActive ? 'text-primary-600 font-medium' : 'text-gray-700'}`}>
                    {org.name}
                  </p>
                  <span className="text-[11px] text-gray-400">
                    {ROLE_LABELS[org.myRole] ?? org.myRole}
                  </span>
                </div>
              </button>
            );
          })}

          {/* Separator + 새 워크스페이스 */}
          <div className="border-t border-gray-100 mt-1 pt-1">
            <button
              onClick={() => {
                setOpen(false);
                navigate('/onboarding/create');
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm
                text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <Plus className="size-4 shrink-0" />
              <span>새 워크스페이스 만들기</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
